import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { useGetPdfLinkForConversationsMutation } from '@circuitry-ai/doc-data';
import { JsonFormViewer, pdfViewPayload, isCompactApplicationType } from '@circuitry-ai/doc-ui';
import { Button, Modal, ModalBody, ModalContent, ModalHeader, Tooltip, useDisclosure } from '@nextui-org/react';

interface ExcelFileViewModalProps {
    excelFileKnowledgeView: { source: { source_id: string; element_refs: { row_data: string, row_num: string }[], file_name: string } };
    advisorId: string;
    onClose: () => void;
}

const ExcelFileViewModal: React.FC<ExcelFileViewModalProps> = ({ excelFileKnowledgeView, onClose, advisorId }) => {
    const { t } = useTranslation();
    const { isOpen, onOpen } = useDisclosure();
    const [getPdfLink, { isLoading: isExcelFileDownloading }] = useGetPdfLinkForConversationsMutation();
    const isCompactView = isCompactApplicationType();
    const fileName = excelFileKnowledgeView?.source?.file_name ?? '';
    const rowNum = excelFileKnowledgeView?.source?.element_refs[0]?.row_num;
    const downloadLabel = t('Download Excel File');

    useEffect(() => {
        if (excelFileKnowledgeView) onOpen();
    }, [excelFileKnowledgeView, onOpen]);

    const handleDownloadFile = async () => {
        const getPdfLinkRequestPayload: pdfViewPayload = {
            advisor_id: advisorId,
            source_ids: [{ source_id: excelFileKnowledgeView.source.source_id }],
        };

        try {
            const urlResponse = await getPdfLink(getPdfLinkRequestPayload).unwrap();
            const downloadUrl =
                urlResponse?.data?.[0]?.full_source_presigned_url || urlResponse?.data?.[0]?.page_labels_presigned_url;
            if (downloadUrl) window.open(downloadUrl, '_blank');
            else console.error('Download URL is missing from response.');
        } catch (error) {
            console.error('Failed to fetch PDF link:', error);
        }
    };

    const parseJSONData = () => {
        try {
            return JSON.parse(excelFileKnowledgeView?.source?.element_refs[0]?.row_data || '{}');
        } catch (error) {
            console.error('Failed to parse row data:', error);
            return {};
        }
    };

    const renderFileName = () => {
        if (isCompactView) {
            return (
                <Tooltip content={fileName} placement="bottom-start">
                    <p className="truncate font-semibold max-w-[10rem] sm:max-w-[14rem]" title={fileName}>
                        {fileName}
                    </p>
                </Tooltip>
            );
        }

        return <p className="font-semibold">{fileName}</p>;
    };

    const renderDownloadButton = () => {
        if (isCompactView) {
            return (
                <Tooltip content={downloadLabel} placement="bottom">
                    <Button
                        id="comment-modal-btn"
                        isIconOnly
                        size="sm"
                        color="primary"
                        onPress={handleDownloadFile}
                        isLoading={isExcelFileDownloading}
                        aria-label={downloadLabel}
                    >
                        <Icon icon="material-symbols:download" width="1.2em" height="1.2em" />
                    </Button>
                </Tooltip>
            );
        }

        return (
            <Button
                id="comment-modal-btn"
                className="mr-4"
                color="primary"
                onPress={handleDownloadFile}
                isLoading={isExcelFileDownloading}
            >
                {downloadLabel}
            </Button>
        );
    };

    return (
        <Modal
            id="comment-modal"
            backdrop="blur"
            isOpen={isOpen}
            onClose={onClose}
            isDismissable={false}
            isKeyboardDismissDisabled={true}
            scrollBehavior="inside"
            size={isCompactView ? 'md' : '5xl'}
            classNames={{
                base: isCompactView
                    ? 'max-h-[85vh] w-[calc(100%-1.5rem)] max-w-lg mx-2'
                    : 'max-h-[80%]',
                header: isCompactView
                    ? 'flex flex-col gap-2 pr-10 sm:flex-row sm:items-start sm:justify-between'
                    : 'flex justify-between gap-1',
                closeButton: isCompactView ? 'top-3 right-3' : undefined,
            }}
        >
            <ModalContent>
                {() => (
                    <>
                        <ModalHeader>
                            <div className="min-w-0 flex-1">
                                {renderFileName()}
                                <span className="text-small text-default-400">
                                    {t('Row')}: {rowNum}
                                </span>
                            </div>
                            {renderDownloadButton()}
                        </ModalHeader>
                        <ModalBody className="overflow-y-auto">
                            <JsonFormViewer jsonData={parseJSONData() ?? {}} />
                        </ModalBody>
                    </>
                )}
            </ModalContent>
        </Modal>
    );
};

export { ExcelFileViewModal };
